import {
  Component,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RegexConstants } from '@wz-constants';
import {
  FormGroupBaseComponent,
  getFormGroupBaseDirective,
  TextInputComponent,
} from '@wz-forms';
import { HealthModels, HealthRiskModels, PolicyModels } from '@wz-models';
import { HealthInsuranceService } from '@wz-services';
import {
  arrayToSelectListItemArray,
  getControlWithValueChanges,
  markAllControlsAsDirty,
} from '@wz-utilities/form.utilities';
import { cleanNullProperties } from '@wz-utilities/object.utilities';
import * as _ from 'lodash';
import {
  Observable,
  throwError as observableThrowError,
  of,
  Subscription,
} from 'rxjs';

@Component({
  selector: 'wz-health-subscription-form',
  templateUrl: './health-subscription-form.component.html',
  styleUrls: ['./health-subscription-form.component.scss'],
  standalone: false,
  providers: [getFormGroupBaseDirective(HealthSubscriptionFormComponent)],
})
export class HealthSubscriptionFormComponent
  extends FormGroupBaseComponent<HealthModels.SubscriptionForm>
  implements OnChanges
{
  @ViewChildren(TextInputComponent)
  inputs = new QueryList<TextInputComponent>();

  /*@ViewChild(HealthVadAlertComponent, { static: true })
  vadAlertComponent: HealthVadAlertComponent;
*/
  @Input() subscriptionData!: HealthModels.SubscriptionForm;
  @Input() risk!: HealthRiskModels.Risk;

  public partnerCtrl!: FormGroup | any;
  public assurePrincipalCtrl!: FormGroup | any;
  public childrenCtrl!: FormArray | any;
  public financialCtrl!: FormGroup | any;
  public quantiemeCtrl!: FormControl | any;
  public package!: PolicyModels.PackageEnum | any;

  quantiemeList = arrayToSelectListItemArray([10, 15], (n) => ({
    text: n.toString(),
    value: n,
  }));
  hide = true;

  subscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    public healthInsuranceService: HealthInsuranceService,
    route: ActivatedRoute,
  ) {
    super();
    this.formGroup = this.createForm();
    this.healthInsuranceService.packageIdFromParams(route);
    this.package = this.healthInsuranceService.packageId;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['subscriptionData']) {
      this.applySubscriptionData(this.subscriptionData);
    }
  }

  applySubscriptionData(subForm: HealthModels.SubscriptionForm) {
    if (subForm) {
      if (subForm.conjoint) {
        this.partnerCtrl = this.getFamilyPersonGroup(subForm.conjoint);
        this.formGroup.setControl('conjoint', this.partnerCtrl);
      } else {
        this.formGroup.removeControl('conjoint');
        this.partnerCtrl = null;
      }

      if (subForm.enfants && subForm.enfants.length > 0) {
        this.childrenCtrl = this.getChildrenArray(subForm.enfants);
        this.formGroup.setControl('enfants', this.childrenCtrl);
      } else {
        this.formGroup.removeControl('enfants');
        this.childrenCtrl = null;
      }

      this.formGroup.patchValue(cleanNullProperties(subForm));
    } else {
      this.formGroup.reset({});
    }
  }

  private createForm() {
    this.assurePrincipalCtrl = this.getAssurePrincipalGroup();
    this.financialCtrl = this.getFinancialGroup();
    return this.fb.group({
      assurePrincipal: this.assurePrincipalCtrl,
      financial: this.financialCtrl,
    });
  }

  getFinancialGroup(): FormGroup {
    this.quantiemeCtrl = this.fb.control(10, [Validators.required]);
    return this.fb.group({
      mainRib: this.getRibGroup(),
      isRefundRibDifferent: false,
      quantieme: this.quantiemeCtrl,
    });
  }

  getRibGroup(): FormGroup {
    return this.fb.group({
      iban: this.fb.control('', [Validators.required, validateIban]),
      civilityHolder: this.fb.control(null, Validators.required),
      firstNameHolder: this.fb.control('', Validators.required),
      lastNameHolder: this.fb.control('', Validators.required),
      address: this.fb.control(null, Validators.required),
    });
  }

  getAssurePrincipalGroup(): FormGroup {
    return this.fb.group({
      numeroSS: this.fb.control('', [Validators.required]),
      numeroOrganismeAffiliation: this.fb.control('', [
        Validators.pattern(RegexConstants.NumeroOrgaAffiliation),
      ]),
    });
  }

  getFamilyPersonGroup(
    person: HealthModels.SubscriptionFamilyPerson,
  ): FormGroup {
    const grp = this.fb.group({
      numeroSS: this.fb.control(person.numeroSS, [Validators.required]),
      numeroOrganismeAffiliation: this.fb.control(
        person.numeroOrganismeAffiliation,
        [Validators.pattern(RegexConstants.NumeroOrgaAffiliation)],
      ),
      prenom: this.fb.control(person.prenom, [Validators.required]),
      nom: this.fb.control(person.nom, [Validators.required]),
      civilite: this.fb.control(person.civilite, [Validators.required]),
      beneficiaryOf: getControlWithValueChanges(
        this.fb,
        person.beneficiaryOf,
        [],
        (fc) => {
          if (fc.value) {
            grp.get('numeroSS')?.disable();
            grp.get('numeroOrganismeAffiliation')?.disable();
          } else {
            grp.get('numeroSS')?.enable();
            grp.get('numeroOrganismeAffiliation')?.enable();
          }
        },
        this.destroy$,
      ),
    });

    return grp;
  }

  getChildrenArray(
    children: HealthModels.SubscriptionFamilyPerson[],
  ): FormArray {
    const array = this.fb.array([]);
    children.forEach((c) =>
      (array as FormArray).push(this.getFamilyPersonGroup(c)),
    );
    return array;
  }

  linkPartnerToYou() {
    this.partnerCtrl.patchValue({
      beneficiaryOf: HealthModels.BeneficiaryTypeEnum.You,
    });

    if (this.childrenCtrl) {
      this.childrenCtrl.controls.forEach((c: any) =>
        this.unlink(c as FormGroup),
      );
    }
  }

  linkToYou(group: AbstractControl) {
    group.patchValue({ beneficiaryOf: HealthModels.BeneficiaryTypeEnum.You });
  }

  linkToPartner(group: AbstractControl) {
    group.patchValue({
      beneficiaryOf: HealthModels.BeneficiaryTypeEnum.Partner,
    });
  }

  unlink(group: AbstractControl) {
    group.patchValue({ beneficiaryOf: null });
  }

  submit(): Observable<HealthModels.SubscriptionForm> {
    if (this.formGroup.valid) {
      // manual check here
      return of(<HealthModels.SubscriptionForm>this.formGroup.getRawValue());
    } else {
      markAllControlsAsDirty(this.formGroup, true);
      const errors = this.inputs.filter((i) => i.errorState);
      if (errors && errors.length > 0) {
        errors[0].focus();
      }

      return observableThrowError('Le formulaire est incomplet ou invalide.');
    }
  }

  get mainRibCtrl() {
    if (this.financialCtrl) {
      return this.financialCtrl.get('mainRib');
    }
    return null;
  }

  getMask() {
    const iban = this.mainRibCtrl.get('iban');
    if (
      iban &&
      iban.value &&
      iban.value.length >= 2 &&
      iban.value.substr(0, 2) === 'FR'
    )
      return 'AA00 AAAA AAAA AAAA AAAA AAAAAAA';
    return null;
  }
}

export const countries = [
  { name: 'AD', length: 24 },
  { name: 'AT', length: 20 },
  { name: 'BE', length: 16 },
  { name: 'BG', length: 22 },
  { name: 'HR', length: 21 },
  { name: 'CY', length: 28 },
  { name: 'CZ', length: 24 },
  { name: 'DK', length: 18 },
  { name: 'EE', length: 20 },
  { name: 'FI', length: 18 },
  { name: 'FR', length: 27 },
  { name: 'DE', length: 22 },
  { name: 'GI', length: 23 },
  { name: 'GR', length: 27 },
  { name: 'VA', length: 22 },
  { name: 'HU', length: 28 },
  { name: 'IS', length: 26 },
  { name: 'IE', length: 22 },
  { name: 'IT', length: 27 },
  { name: 'LV', length: 21 },
  { name: 'LI', length: 21 },
  { name: 'LT', length: 20 },
  { name: 'LU', length: 20 },
  { name: 'MT', length: 31 },
  { name: 'MC', length: 27 },
  { name: 'NL', length: 18 },
  { name: 'NO', length: 15 },
  { name: 'PL', length: 28 },
  { name: 'PT', length: 25 },
  { name: 'RO', length: 24 },
  { name: 'SM', length: 27 },
  { name: 'SK', length: 24 },
  { name: 'SI', length: 19 },
  { name: 'ES', length: 24 },
  { name: 'SE', length: 24 },
  { name: 'CH', length: 21 },
  { name: 'GB', length: 22 },
];

function validateIban(c: AbstractControl): ValidationErrors | null {
  if (c && c.value && c.value.length >= 2) {
    let country = _.find(
      countries,
      (country) => country.name === c.value.substr(0, 2),
    );

    if (!country) {
      return {
        iban: {
          valid: false,
        },
      };
    }

    if (c.value.length !== country.length) {
      return {
        iban: {
          valid: false,
        },
      };
    }
  }
  return null;
}
